'use client'

import { Button, Dialog, InputTextArea, Navigation, Rating, Spacer, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { submitAutomatedSupportFeedback } from 'data/api/submit-automated-support-feedback'
import useHelpCenterSessionTracking from 'hooks/useHelpCenterSessionTracking'
import { FeedbackType, CURRENT_SURVEY_VERSION } from 'constants/automated-feedback'
import { ConversationActionType } from '@marketplace-web/messaging/conversation-action-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  autoreplyFeedbackClosed,
  autoreplyFeedbackV1Viewed,
  chatbotFeedbackClosed,
  chatbotFeedbackV1Viewed,
} from '_libs/common/event-tracker/events'

import { getFeedbackType } from './utils'

type Props = {
  isOpen: boolean
  action?: ConversationActionType
}

const AutomatedResponseFeedbackModal = ({ isOpen, action }: Props) => {
  const translate = useTranslate()
  const [rating, setRating] = useState<number>(0)
  const [comment, setComment] = useState<string>('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const [isModalOpen, setIsModalOpen] = useState(isOpen)

  const userId = useSession().user?.id
  const { conversation } = useConversationContext()
  const conversationId = conversation?.id
  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const wasModalSeen = useRef(false)
  const { track } = useTracking()
  const feedbackType = getFeedbackType(action)

  const trackModalOpening = () => {
    if (!helpCenterSessionId || !conversationId || wasModalSeen.current) return
    wasModalSeen.current = true

    const trackedEventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
    }

    if (feedbackType === FeedbackType.Chatbot) {
      track(chatbotFeedbackV1Viewed(trackedEventParams))
    } else {
      track(autoreplyFeedbackV1Viewed(trackedEventParams))
    }
  }

  const trackModalClosing = () => {
    if (!helpCenterSessionId || !conversationId) return

    const trackedEventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
    }

    if (feedbackType === FeedbackType.Chatbot) {
      track(chatbotFeedbackClosed(trackedEventParams))
    } else {
      track(autoreplyFeedbackClosed(trackedEventParams))
    }
  }

  useEffect(() => {
    if (!isOpen) return
    trackModalOpening()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, helpCenterSessionId])

  useEffect(() => {
    if (!rating) return

    inputRef.current?.focus()
  }, [rating])

  function handleRatingChange(selectedRating: number) {
    setRating(selectedRating)
  }

  function handleMessageTyping({ target }: ChangeEvent<HTMLTextAreaElement>) {
    setComment(target.value)
  }

  function handleCloseModal() {
    trackModalClosing()
    setRating(0)
    setComment('')
    setIsModalOpen(false)
  }

  async function handleSubmitUserFeedback() {
    if (!userId || !conversationId || !helpCenterSessionId) return

    setIsSubmitting(true)

    const response = await submitAutomatedSupportFeedback({
      userId,
      conversationId: conversationId.toString(),
      rating,
      comment,
      surveyVersion: CURRENT_SURVEY_VERSION,
      helpCenterSessionId,
      feedbackFor: feedbackType,
    })

    if ('errors' in response) return

    handleCloseModal()
  }

  function renderStarRating() {
    return (
      <div className="u-ui-padding-large">
        <div className="u-flexbox u-justify-content-center">
          <Rating
            isInteractive
            onSelect={handleRatingChange}
            size={Rating.Size.Large}
            value={rating}
            testId="automated-feedback-star-rating"
            aria={{
              'aria-labelledby': translate('user.a11y.rating', { rating, max_rating: 5 }),
            }}
          />
        </div>
      </div>
    )
  }

  function renderActionButtons() {
    if (!rating) return null

    return (
      <div className="feedback-modal__actions">
        <Button
          text={translate('help_center_feedback.actions.submit')}
          styling={Button.Styling.Filled}
          onClick={handleSubmitUserFeedback}
          isLoading={isSubmitting}
        />
      </div>
    )
  }

  function renderTextArea() {
    if (!rating) return null

    return (
      <InputTextArea
        ref={inputRef}
        maxRows={5}
        value={comment}
        name="automated_feedback_comment"
        placeholder={translate('help_center_feedback.star_rating_modal.placeholder')}
        onChange={handleMessageTyping}
        testId="automated_feedback_comment"
      />
    )
  }

  return (
    <Dialog testId="automated-feedback-modal" show={isModalOpen} hasScrollableContent>
      <div className="u-fill-width">
        <Navigation
          theme="transparent"
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={handleCloseModal}
              iconName={X24}
              testId="automated-feedback-resolved-close-button"
              aria={{ 'aria-label': translate('help_center_feedback.actions.close') }}
            />
          }
        />
        <ScrollableArea>
          <Text
            as="h1"
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
            text={translate('help_center_feedback.star_rating_modal.title')}
            type={Text.Type.Heading}
          />
          <Text
            as="h3"
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
            text={translate('help_center_feedback.star_rating_modal.subtitle')}
            type={Text.Type.Subtitle}
          />
          {renderStarRating()}
          {renderTextArea()}
          {renderActionButtons()}
        </ScrollableArea>
        <Spacer size={Spacer.Size.Large} />
      </div>
    </Dialog>
  )
}

export default memo(AutomatedResponseFeedbackModal)
